import Button from '@molecules/Button';
import { BELoadingButton, ButtonProps } from '@molecules/Button/Button';
import type { MutableRefObject } from 'react';
import { useCallback, useMemo, useRef } from 'react';
import type { FieldValues, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { MutationFunction, UseMutationOptions } from 'react-query';
import { useMutation } from 'react-query';

import FormFactory from '.';

export type ModalButtonsProps = {
  onCancel?: () => void;
  okText?: string;
  minWidth?: number;
};

export default function useForm<
  TData = unknown,
  TError = unknown,
  T extends FieldValues = any,
  TContext = unknown
>(
  mutationFn: MutationFunction<TData, T>,
  options?: UseMutationOptions<TData, TError, T, TContext>
) {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  const formRef = useRef<UseFormReturn<T>>() as MutableRefObject<
    // eslint-disable-next-line
		UseFormReturn<T>
  >;
  const { t } = useTranslation();

  const mutation = useMutation(mutationFn, options);

  const onSaveRef = useRef((() => {
    // prevent multiple consecutive clicks on save
    if (!formRef?.current?.formState?.isSubmitting) {
      return formRef?.current?.handleSubmit((data) =>
        mutation.mutateAsync(data as never)
      )();
    }
  }) as () => Promise<void>);

  const onCancel = useCallback(
    (callback?: () => void) => () => {
      formRef.current?.reset();
      callback?.();

      return true;
    },
    []
  );

  const FormButton = useMemo(
    () => ({
      // noinspection JSUnusedGlobalSymbols
      Cancel: ({
        onClick,
        className,
        minWidth = 110,
        ...props
      }: Omit<ButtonProps, 'children'>) => (
        <Button
          className={className}
          minWidth={minWidth}
          {...props}
          onClick={onCancel(onClick as () => void)}
        >
          {t('common:cancel')}
        </Button>
      ),
      Save: ({
        children,
        minWidth,
        ...props
      }: Omit<ButtonProps, 'onClick' | 'children'> & {
        children?: string;
        disabled: boolean;
      }) => (
        <BELoadingButton
          minWidth={minWidth}
          onClick={onSaveRef.current}
          {...props}
        >
          {children || t('save')}
        </BELoadingButton>
      ),
    }),
    [onCancel, t]
  );

  const modalButtons = useCallback(
    ({ onCancel: onClickCancel, okText, minWidth }: ModalButtonsProps) => (
      <>
        <FormButton.Save minWidth={minWidth} disabled={false}>
          {okText}
        </FormButton.Save>
        <FormButton.Cancel minWidth={minWidth} onClick={onClickCancel} />
      </>
    ),
    [FormButton]
  );

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const formFactory = useMemo(() => new FormFactory<T>({ formRef }), []);

  return useMemo(
    () => ({
      formRef,
      onSave: onSaveRef.current,
      FormButton,
      modalButtons,
      mutation,
      ...formFactory,
    }),
    [FormButton, modalButtons, onCancel, mutation, formFactory]
  );
}

export type UseFormResult<T extends { [K in keyof T]: T[K] }> = Pick<
  ReturnType<typeof useForm>,
  'onSave' | 'FormButton' | 'modalButtons'
> & { formRef: MutableRefObject<UseFormReturn<T>> } & FormFactory<T>;
